import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { withSearch } from '@elastic/react-search-ui';
import Select, { components } from 'react-select';
import { css } from '@linaria/core';
import classNames from 'classnames';
import { PulseLoader } from 'react-spinners';
import { useConnector } from '../connectors/ConnectorContext';
import { getExistingFilterDataFirstValue } from '../utils';
import { useDebounce } from '../hooks/useDebounce';
import { trackFilterSelection } from '../utils/dataLayer';
import {
  LOCATION_FILTER_FIELD,
  LOCATION_DISTANCE_FILTER_FIELD,
} from '../utils/uiFilters';
import LocationPinIcon from '../icons/LocationPin';
import ChevronDownIcon from '../icons/ChevronDown';
import CloseIcon from '../icons/Close';

export const DISTANCE_OPTIONS = [
  { value: '0', label: 'This area only' },
  { value: '0.5mi', label: 'Within 0.5mi' },
  { value: '5mi', label: 'Within 5mi' },
  { value: '10mi', label: 'Within 10mi' },
];

const label = css`
  display: block;
  color: #fff;
  font-weight: 400;
  font-size: 18px;
  line-height: 30px;
  margin-bottom: 10px;
`;

const focusShadow = `0 0 0 3px #fd0, inset 0 0 0 3px #1a1a1a`;

const group = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;

  @media (min-width: 800px) {
    flex-direction: row;
    align-items: center;
    gap: 0;
    height: 52px;
    background: #fff;
    border: 1px solid #1a1a1a;
    border-radius: 5px;
  }
`;

const boxBase = `
  display: flex;
  align-items: center;
  height: 52px;
  background: #fff;
  border: 1px solid #1a1a1a;
  border-radius: 5px;

  @media (min-width: 800px) {
    height: auto;
    background: transparent;
    border: none;
    border-radius: 0;
  }
`;

const locationBox = css`
  ${boxBase}
  width: 100%;

  @media (min-width: 800px) {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

const distanceBox = css`
  ${boxBase}
  width: 100%;

  @media (min-width: 800px) {
    flex: 0 0 auto;
    width: 177px;
  }
`;

const locationBoxFocused = css`
  box-shadow: ${focusShadow};

  @media (min-width: 800px) {
    border-radius: 5px 0 0 5px;
  }
`;

const distanceBoxFocused = css`
  box-shadow: ${focusShadow};

  @media (min-width: 800px) {
    border-radius: 0 5px 5px 0;
  }
`;

const pinIcon = css`
  fill: #1a1a1a;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin: 0 0 0 15px;
`;

const wrapper = css`
  flex: 535 1 0;
  min-width: 0;
`;

const locationField = css`
  flex: 1 1 auto;
  min-width: 0;
`;

const distanceField = css`
  width: 100%;
`;

const divider = css`
  display: none;

  @media (min-width: 800px) {
    display: block;
    width: 1px;
    height: 32px;
    background: #e5dbd1;
    flex-shrink: 0;
  }
`;

const controlBase = `
  cursor: pointer !important;
  font-size: 18px;
  line-height: 24px;
  min-height: 50px;
  width: 100%;

  input:focus {
    box-shadow: none !important;
  }
`;

const control = css`
  ${controlBase}
  padding: 0 15px;
`;

const locationControl = css`
  ${controlBase}
  padding: 0 6px 0 15px;
`;

const valueContainer = css`
  flex-wrap: nowrap;
`;

const placeholder = css`
  color: #6f6f6f;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const singleValue = css`
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const menu = css`
  border: 1px solid #e5dbd1;
  background: #fff;
  box-shadow: 4px 4px 4px 0px #00000033;
  border-radius: 5px;
  margin-top: 5px;
`;

const menuPortal = css`
  z-index: 9999;
`;

const menuList = css`
  border-radius: 5px;
  overflow: hidden;
  padding: 0;
`;

const noOptionsMessage = css`
  padding: 8px 15px;
  text-align: left;
`;

const option = css`
  cursor: pointer !important;
  padding: 8px 15px;
  line-height: 24px;

  &:hover {
    background-color: #e6e6e6;
  }
`;

const optionFocused = css`
  background-color: #e6e6e6;
`;

const optionSelected = css`
  background-color: #e6e6e6;
  font-weight: 600;
`;

const dropdownIcon = css`
  fill: #1a1a1a;
  width: 14px;
  transition: transform 300ms ease-out;

  &[data-open='true'] {
    transform: rotate(180deg);
  }
`;

const DropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    <ChevronDownIcon
      className={dropdownIcon}
      data-open={props.selectProps.menuIsOpen}
    />
  </components.DropdownIndicator>
);

const clearButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: background-color 200ms ease;

  &:hover {
    background-color: #f9f6f4;
  }

  &:focus {
    box-shadow: 0 0 0 3px #ffdd00;
  }

  svg {
    fill: #1a1a1a;
    width: 16px;
    height: 16px;
  }
`;

// A button rather than react-select's indicator div, so it is a tab stop with an
// accessible name.
const ClearIndicator = ({ clearValue }) => (
  <button
    aria-label="Clear location"
    className={clearButton}
    onClick={clearValue}
    onMouseDown={event => event.preventDefault()}
    type="button"
  >
    <CloseIcon />
  </button>
);

const LoadingIndicator = () => (
  <PulseLoader color="#1a1a1a" margin={1} size={5} />
);

const selectClassNames = {
  control: () => control,
  valueContainer: () => valueContainer,
  placeholder: () => placeholder,
  singleValue: () => singleValue,
  menu: () => menu,
  menuPortal: () => menuPortal,
  menuList: () => menuList,
  noOptionsMessage: () => noOptionsMessage,
  loadingMessage: () => noOptionsMessage,
  option: ({ isFocused, isSelected }) =>
    [option, isFocused && optionFocused, isSelected && optionSelected]
      .filter(Boolean)
      .join(' '),
};

const locationSelectClassNames = {
  ...selectClassNames,
  control: () => locationControl,
};

// Force the control to fill the 52px field (react-select's unstyled base caps
// it at 38px) so the portalled menu anchors to the field's bottom, not mid-field.
const selectStyles = {
  control: base => ({ ...base, minHeight: 52, height: 52 }),
};

function findDistanceOption(value) {
  return (
    DISTANCE_OPTIONS.find(opt => opt.value === value) || DISTANCE_OPTIONS[0]
  );
}

function LocationSearch({
  actionsRef,
  filters,
  setFilter,
  removeFilter,
  subtitle = 'Where would you like to find it',
}) {
  const connector = useConnector();
  // Pending values: staged locally, only applied as filters when the shared
  // Search button is pressed (Header calls apply() via actionsRef).
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [distance, setDistance] = useState(DISTANCE_OPTIONS[0]);
  // Focus is tracked per part rather than with :focus-within, so the clear
  // button inside the location part only highlights itself.
  const [isLocationFocused, setIsLocationFocused] = useState(false);
  const [isDistanceFocused, setIsDistanceFocused] = useState(false);
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  // Incremented whenever in-flight suggestion responses become irrelevant, so
  // out-of-order resolutions can't show stale options.
  const requestSeqRef = useRef(0);
  // Last APPLIED (submitted) values, so unrelated filter changes don't clobber
  // a pending selection.
  const appliedRef = useRef({ location: undefined, distance: undefined });

  useEffect(() => {
    const locationValue = getExistingFilterDataFirstValue(
      LOCATION_FILTER_FIELD,
      filters,
    );
    const distanceValue = getExistingFilterDataFirstValue(
      LOCATION_DISTANCE_FILTER_FIELD,
      filters,
    );

    if (
      locationValue === appliedRef.current.location &&
      distanceValue === appliedRef.current.distance
    ) {
      return;
    }

    appliedRef.current = { location: locationValue, distance: distanceValue };

    if (locationValue) {
      setSelectedLocation({ value: locationValue, label: locationValue });
      setDistance(findDistanceOption(distanceValue));
    } else {
      setSelectedLocation(null);
      setDistance(DISTANCE_OPTIONS[0]);
    }
  }, [filters]);

  // Debounce so we don't hit the GIS endpoint on every keystroke.
  const debouncedInputValue = useDebounce(inputValue, 400);

  useEffect(() => {
    const term = debouncedInputValue.trim();
    if (!term) {
      return;
    }

    const seq = ++requestSeqRef.current;
    setIsFetching(true);
    connector
      .onLocationSuggestions(term)
      .then(suggestions => {
        if (seq !== requestSeqRef.current) return;
        const nextOptions = suggestions.map(text => ({
          value: text,
          label: text,
        }));
        setOptions(nextOptions);
        setIsFetching(false);
        // Only reveal the dropdown once suggestions exist (no empty-state menu).
        setIsLocationMenuOpen(nextOptions.length > 0);
      })
      .catch(() => {
        if (seq !== requestSeqRef.current) return;
        setOptions([]);
        setIsFetching(false);
        setIsLocationMenuOpen(false);
      });
  }, [connector, debouncedInputValue]);

  const resetSuggestions = () => {
    requestSeqRef.current += 1;
    setOptions([]);
    setIsFetching(false);
    setIsLocationMenuOpen(false);
  };

  const clearLocation = () => {
    setSelectedLocation(null);
    setDistance(DISTANCE_OPTIONS[0]);
    setInputValue('');
    resetSuggestions();
    // The clear button unmounts with the value, so focus would otherwise be lost.
    document.getElementById('location-input')?.focus();

    // Only re-run the search when the location was actually applied; clearing
    // a pending (never-submitted) pick must not dispatch a request.
    if (getExistingFilterDataFirstValue(LOCATION_FILTER_FIELD, filters)) {
      removeFilter(LOCATION_FILTER_FIELD);
    }
    if (
      getExistingFilterDataFirstValue(LOCATION_DISTANCE_FILTER_FIELD, filters)
    ) {
      removeFilter(LOCATION_DISTANCE_FILTER_FIELD);
    }
  };

  const applyPendingSelection = () => {
    const appliedLocation = getExistingFilterDataFirstValue(
      LOCATION_FILTER_FIELD,
      filters,
    );
    const appliedDistance = getExistingFilterDataFirstValue(
      LOCATION_DISTANCE_FILTER_FIELD,
      filters,
    );

    if (selectedLocation) {
      if (selectedLocation.value !== appliedLocation) {
        setFilter(LOCATION_FILTER_FIELD, selectedLocation.value);
        trackFilterSelection(LOCATION_FILTER_FIELD, selectedLocation.value);
      }
      if (distance.value !== appliedDistance) {
        setFilter(LOCATION_DISTANCE_FILTER_FIELD, distance.value);
        trackFilterSelection(LOCATION_DISTANCE_FILTER_FIELD, distance.value);
      }
    } else {
      if (appliedLocation) {
        removeFilter(LOCATION_FILTER_FIELD);
      }
      if (appliedDistance) {
        removeFilter(LOCATION_DISTANCE_FILTER_FIELD);
      }
    }
  };

  useImperativeHandle(actionsRef, () => ({
    getLocationValue: () => selectedLocation?.value || null,
    apply: applyPendingSelection,
    clear: clearLocation,
  }));

  const handleLocationChange = data => {
    if (data) {
      setSelectedLocation(data);
      resetSuggestions();
    } else {
      clearLocation();
    }
  };

  const handleLocationInputChange = (value, meta) => {
    if (meta.action === 'input-change') {
      setInputValue(value);
      if (!value) {
        resetSuggestions();
      }
    } else {
      // set-value / input-blur / menu-close: react-select clears the input;
      // drop any in-flight fetch so the menu can't pop open unfocused.
      setInputValue('');
      requestSeqRef.current += 1;
      setIsFetching(false);
    }
  };

  const handleDistanceChange = option => {
    // Staged only; applied together with the location on Search.
    setDistance(option || DISTANCE_OPTIONS[0]);
  };

  return (
    <div className={wrapper}>
      <label
        className={label}
        dangerouslySetInnerHTML={{ __html: subtitle }}
        htmlFor="location-input"
      />
      <div className={group}>
        <div
          className={classNames(
            locationBox,
            isLocationFocused && locationBoxFocused,
          )}
        >
          <LocationPinIcon aria-hidden="true" className={pinIcon} />
          <div className={locationField}>
            <Select
              isClearable
              unstyled
              aria-label="Location"
              classNames={locationSelectClassNames}
              components={{
                ClearIndicator,
                DropdownIndicator: null,
                IndicatorSeparator: null,
                LoadingIndicator,
              }}
              filterOption={() => true}
              inputId="location-input"
              inputValue={inputValue}
              isLoading={isFetching}
              menuIsOpen={isLocationMenuOpen}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              onBlur={() => setIsLocationFocused(false)}
              onChange={handleLocationChange}
              onFocus={() => setIsLocationFocused(true)}
              onInputChange={handleLocationInputChange}
              onMenuClose={() => setIsLocationMenuOpen(false)}
              openMenuOnClick={false}
              openMenuOnFocus={false}
              options={options}
              placeholder="Town, city or county"
              styles={selectStyles}
              value={selectedLocation}
            />
          </div>
        </div>
        <div aria-hidden="true" className={divider} />
        <div
          className={classNames(
            distanceBox,
            isDistanceFocused && distanceBoxFocused,
          )}
        >
          <div className={distanceField}>
            <Select
              unstyled
              aria-label="Search distance"
              classNames={selectClassNames}
              components={{ DropdownIndicator, IndicatorSeparator: null }}
              inputId="location-distance-input"
              isSearchable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              onBlur={() => setIsDistanceFocused(false)}
              onChange={handleDistanceChange}
              onFocus={() => setIsDistanceFocused(true)}
              options={DISTANCE_OPTIONS}
              styles={selectStyles}
              value={distance}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default withSearch(({ filters, setFilter, removeFilter }) => ({
  filters,
  setFilter,
  removeFilter,
}))(LocationSearch);
